/**
 * One run, live (admin-design-r1: a record stays a full page). The header says what, how far and how fresh; each step
 * is a row with its status, times, the white-glove actions the runner allows (domain/stepActions.ts) and what it
 * recorded. Follows the run's event stream, falling back to polling (infra/watchRun.ts).
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { elapsed, idValue, TEMPLATE_LABEL, when } from '../../domain/format'
import type { Run, RunSnapshot, Step, StepAction } from '../../domain/schemas'
import { stepActions } from '../../domain/stepActions'
import { statusLook } from '../../domain/tones'
import { ApiError } from '../../infra/api'
import { watchRun, type WatchState } from '../../infra/watchRun'
import { Button, Chip, DataTable, EmptyState, ErrorNote, Inline, LiveBadge, Screen, Stack, Text } from '../../ui'
import { useApi } from '../api-context'
import { StepTitle } from '../components/StepTitle'
import { useLoad } from '../useLoad'
import { useNow } from '../useNow'

const LIVE: Record<WatchState, { mode: 'poll' | 'stream' | 'paused' | 'reconnecting'; label: string }> = {
  connecting: { mode: 'reconnecting', label: 'connecting' },
  live: { mode: 'stream', label: 'live' },
  reconnecting: { mode: 'reconnecting', label: 'reconnecting' },
  polling: { mode: 'poll', label: 'polling' },
  stopped: { mode: 'paused', label: 'not live' },
}

export function RunPage() {
  const { id = '' } = useParams()
  const { api } = useApi()
  const initial = useLoad(() => api.run(id), [api, id])
  const [run, setRun] = useState<Run | null>(null)
  const [active, setActive] = useState(false)
  const [live, setLive] = useState<WatchState>('connecting')
  const [liveError, setLiveError] = useState<string>()
  const [pending, setPending] = useState<string | null>(null)
  const [actionError, setActionError] = useState<{ key: string; message: string } | null>(null)

  const founder = useLoad(() => (run ? api.founder(run.founderId) : Promise.resolve(null)), [api, run?.founderId])

  useEffect(() => {
    if (initial.data) {
      setRun(initial.data.run)
      setActive(initial.data.active)
    }
  }, [initial.data])

  // Follow the run once it has loaded. Snapshots replace status, error and steps; `active` follows status.
  const loaded = Boolean(initial.data)
  useEffect(() => {
    if (!loaded) return
    const merge = (s: RunSnapshot) => {
      setRun((r) => (r && r.id === s.id ? { ...r, status: s.status, error: s.error, updatedAt: s.updatedAt, steps: s.steps, finishedAt: s.status === 'done' ? (r.finishedAt ?? s.updatedAt) : s.status === 'running' ? null : r.finishedAt } : r))
      setActive(s.status === 'running')
    }
    const w = watchRun({
      open: (signal) => api.runEvents(id, signal),
      poll: async () => {
        const r = await api.run(id)
        setActive(r.active)
        return r.run
      },
      onRun: merge,
      onState: (st, e) => (setLive(st), setLiveError(e?.message)),
    })
    return () => w.stop()
  }, [api, id, loaded])

  const now = useNow(1000, run?.status === 'running')

  async function act(step: Step, action: StepAction) {
    setPending(`${step.key}:${action}`)
    setActionError(null)
    try {
      const r = await api.stepAction(id, step.key, action)
      setRun(r)
    } catch (e) {
      setActionError({ key: step.key, message: e instanceof ApiError ? e.message : String(e) })
    } finally {
      setPending(null)
    }
  }

  const f = founder.data?.founder
  const query = { data: run ?? undefined, error: run ? undefined : initial.error, loading: !run, reload: initial.reload }
  const settled = run ? run.steps.filter((s) => s.status === 'done' || s.status === 'skipped').length : 0
  const look = run ? statusLook(run.status) : null

  return (
    <Screen
      title={run ? `${TEMPLATE_LABEL[run.template]} run` : 'Run'}
      crumb={f ? { label: f.name, to: `/founders/${f.id}` } : { label: 'Runs', to: '/runs' }}
      after={
        run &&
        look && (
          <>
            <Chip tone={look.tone} light={look.light}>{run.status}</Chip>
            <LiveBadge mode={LIVE[live].mode} label={LIVE[live].label} />
          </>
        )
      }
      description={
        run && (
          <Text mono size="sm" tone="muted">
            {[`started ${when(run.createdAt, now)}`, elapsed(run.createdAt, run.finishedAt, now, run.status === 'running' ? null : run.updatedAt), `${settled} of ${run.steps.length} settled`, `by ${run.createdBy}`].filter(Boolean).join(' · ')}
          </Text>
        )
      }
      query={query}
      skeleton="record"
    >
      {(r) => (
        <Stack>
          {r.error && r.status === 'failed' && <ErrorNote title="The run stopped." reason={r.error} />}
          {live === 'stopped' && liveError && <ErrorNote title="Live updates stopped." reason={liveError} />}
          <DataTable
            label="Steps"
            rows={r.steps}
            rowKey={(s) => s.key}
            attention={(s) => s.status === 'failed'}
            empty={<EmptyState title="No steps.">This template has nothing to do.</EmptyState>}
            columns={[
              {
                key: 'step',
                label: 'Step',
                narrow: 'main',
                render: (s) => <StepTitle template={r.template} step={s} n={r.steps.indexOf(s) + 1} />,
              },
              { key: 'status', label: 'Status', narrow: 'end', render: (s) => <Chip tone={statusLook(s.status).tone} light={statusLook(s.status).light}>{s.status}</Chip> },
              {
                key: 'when',
                label: 'When',
                render: (s) =>
                  s.startedAt ? (
                    <Text mono size="xs" tone="muted">
                      {[when(s.startedAt, now), elapsed(s.startedAt, s.finishedAt, now, s.status === 'running' ? null : (s.finishedAt ?? r.updatedAt)), s.by ? `by ${s.by}` : null].filter(Boolean).join(' · ')}
                    </Text>
                  ) : (
                    <Text size="sm" tone="muted">–</Text>
                  ),
              },
              {
                key: 'actions',
                label: 'Actions',
                narrowLabel: false,
                render: (s) => {
                  const rules = stepActions(r, s.key, { active })
                  return (
                    <Inline gap="xs">
                      {(['run', 'skip', 'done'] as const).map((a) => {
                        const rule = rules[a]
                        const primary = (a === 'run' && s.status === 'failed') || (a === 'done' && s.status === 'manual')
                        return (
                          <Button key={a} size="xs" variant={primary ? 'ink' : 'soft'} disabled={!rule.enabled || pending !== null} title={rule.reason} onClick={() => act(s, a)}>
                            {pending === `${s.key}:${a}` ? '…' : rule.label}
                          </Button>
                        )
                      })}
                    </Inline>
                  )
                },
              },
              {
                key: 'detail',
                label: 'Recorded',
                render: (s) => {
                  const ids = Object.entries(s.ids ?? {})
                  if (!ids.length && !s.error && actionError?.key !== s.key) return <Text size="sm" tone="muted">–</Text>
                  return (
                    <Stack gap="xs">
                      {ids.map(([k, v]) => (
                        <Text key={k} mono size="xs">{`${k}  ${idValue(v)}`}</Text>
                      ))}
                      {s.error && <Text size="sm" tone="down">{s.error}</Text>}
                      {actionError?.key === s.key && (
                        <span role="alert">
                          <Text size="sm" tone="down">{actionError.message}</Text>
                        </span>
                      )}
                    </Stack>
                  )
                },
              },
            ]}
          />
        </Stack>
      )}
    </Screen>
  )
}
